import moment from "moment";
import {formatCurrencyValue} from '../../util/PaymentUtils';
import {BookingTransactionTypeLabel, BookingTransactionType, DateFormats} from "../../components/constants/securspace-constants";
  import {generateSupplierInvoicesLinkFromTransaction} from "../../util/LinkUtils";
import type {InvoiceTransactionVO} from "../../types/model/valueObject/invoice/InvoiceTransactionVO";
import type {SelectInputProps} from "../../components/SortFilter/interfaces";

type PayoutInvoice = Pick<InvoiceTransactionVO, 'transactionNumber' | 'supplierAmount' | 'createdOn' | 'serviceDates'
  | 'paymentCreatedOn' | 'transactionType' | 'bookingNumber' | 'buyerCompanyName'>;

/** An invoice after formatInvoices: one display value per column, in column order. */
type FormattedPayoutInvoice = {
  transactionNumber: number;
  supplierAmount: string | number;
  createdOn: string;
  serviceDates: string;
  paymentCreatedOn: string;
  agsServiceDates: string;
  serviceMonth: string;
  transactionType: string;
  bookingNumber: string;
  buyerCompanyName: string;
};

type PayoutValueKey = keyof FormattedPayoutInvoice;

interface PayoutValue {
  label: string;
  /** Show the column on regular invoice cards. */
  reg: boolean;
  /** Show the column on AGS fee invoice cards. */
  ags: boolean;
  formatter: (invoice: PayoutInvoice) => FormattedPayoutInvoice[PayoutValueKey];
  /** GridCard calls this with the clicked formatted invoice. */
  onClick?: (payout: FormattedPayoutInvoice) => void;
  reportLabel?: string;
}

interface PayoutHeaderCell {
  id: PayoutValueKey;
  label: string;
  onClick?: (payout: FormattedPayoutInvoice) => void;
}

const onInvoiceClick = (payout: Pick<FormattedPayoutInvoice, 'transactionNumber' | 'createdOn'>): void => {
  window.open(generateSupplierInvoicesLinkFromTransaction(payout?.transactionNumber, payout?.createdOn));
}

const onBookingClick = (payout: Pick<FormattedPayoutInvoice, 'bookingNumber'>): void => {
  window.open(`/bookings/supplier-bookings?bookingNumber=${payout?.bookingNumber}`);
}

const payoutValues: Readonly<Record<PayoutValueKey, PayoutValue>> = Object.freeze({
  transactionNumber:  { label: 'Invoice Number',  reg: true,  ags: true,  formatter: (invoice) => invoice.transactionNumber, onClick: onInvoiceClick },
  supplierAmount:     { label: 'Amount',          reg: true,  ags: true,  formatter: (invoice) => formatCurrencyValue(invoice.supplierAmount) },
  createdOn:          { label: 'Charged Date',    reg: true,  ags: false, formatter: (invoice) => invoice.createdOn },
  serviceDates:       { label: 'Service Dates',   reg: true,  ags: false, formatter: (invoice) => invoice.serviceDates },
  paymentCreatedOn:   { label: 'Payment Date',    reg: true,  ags: false, formatter: (invoice) => invoice.paymentCreatedOn },
  agsServiceDates:    { label: 'Payment Date',    reg: false, ags: true,  formatter: (invoice) => invoice.transactionType === BookingTransactionType.AGS_FEE ? moment(invoice.serviceDates).format(DateFormats.DAY) : "—", reportLabel: 'Ags Payment Date' },
  serviceMonth:       { label: 'Service Month',   reg: false, ags: true,  formatter: (invoice) => moment(invoice.createdOn).format(DateFormats.MONTH_YEAR) },
  transactionType:    { label: 'Type',            reg: true,  ags: true,  formatter: (invoice) => BookingTransactionTypeLabel[invoice.transactionType as keyof typeof BookingTransactionTypeLabel] },
  bookingNumber:      { label: 'Booking Number',  reg: true,  ags: false, formatter: (invoice) => invoice.bookingNumber, onClick: onBookingClick },
  buyerCompanyName:   { label: 'Buyer',           reg: true,  ags: false, formatter: (invoice) => invoice.buyerCompanyName },
});

// Object.keys is typed string[], but payoutValues has exactly the PayoutValueKey keys, in column order.
const payoutValueKeys = Object.keys(payoutValues) as PayoutValueKey[];

const constructHeaderCells = (filterFlag: 'reg' | 'ags'): PayoutHeaderCell[] => {
  return payoutValueKeys.filter(key => payoutValues[key][filterFlag]).map(key => {
    const headerCell: PayoutHeaderCell = {
      id: key,
      label: payoutValues[key].label
    };

    if (payoutValues[key].onClick) {
      headerCell.onClick = payoutValues[key].onClick;
    }
    return headerCell;
  });
}

export const headerCells = constructHeaderCells("reg");

export const agsHeaderCells = constructHeaderCells("ags");

export const sortByOptions: SelectInputProps[] = [
    {value: "none", label: "None"},
    ...headerCells.map((cell) => ({value: cell.id, label: cell.label})),
];

export const formatInvoices = (invoices: PayoutInvoice[]): FormattedPayoutInvoice[] => {
  return invoices.map((invoice) => {
    const formattedInvoice: Partial<Record<PayoutValueKey, string | number>> = {};
    for (const invoiceItem of payoutValueKeys) {
      formattedInvoice[invoiceItem] = payoutValues[invoiceItem].formatter(invoice);
    }
    // The loop above fills in every column.
    return formattedInvoice as FormattedPayoutInvoice;
  })
};

export const mapInvoicesForCsv = (invoices: FormattedPayoutInvoice[]): FormattedPayoutInvoice[] | (string | number)[][] => {
  if (!invoices || invoices.length === 0) {
    return invoices;
  }

  const csvData: (string | number)[][] = [(Object.keys(invoices[0]) as PayoutValueKey[]).map((key) => payoutValues[key].reportLabel ? payoutValues[key].reportLabel : payoutValues[key].label)];

  invoices.forEach((invoice) => csvData.push(Object.values(invoice)));

  return csvData;
};